import { useTranslation } from "react-i18next";
import { useTimedSelector } from "../hooks";
import { RootState, useAppDispatch, useAppSelector } from "../store";
import { useEffect, useState } from "react";
import { rateConversation } from "../actions/sharedActions";
import { selectConversationId } from "../slices/chatSlice";
import log from "../log";

// Rating visibility selector with postponement logic
export const selectShowRating = (state: RootState) => {
  const now = Date.now();
  const currentConversation = state.conversations.data.find(
    (c) => c.conversation_id === state.chat.conversationId
  );
  const hasMessageAfterRating =
    currentConversation?.rating_offered_at &&
    state.chat.messages
      .filter((m) => m.timestamp !== undefined)
      .some((m) => m.timestamp! > currentConversation.rating_offered_at!);

  const blockers = {
    "Conversation not set": currentConversation === undefined,
    "Generating messages": state.chat.isLoading,
    "API error present": state.chat.apiError !== "",
    "Postponed until a future time": !!(
      state.chat.ratingPostponedUntil && now < state.chat.ratingPostponedUntil
    ),
    "Don't show again if already rated (message time is after rating offering)":
      hasMessageAfterRating && currentConversation?.rating_score > 0,
    "Chatbot is not Visible and Open": !(
      state.launcher.isOpen && state.launcher.isShow
    ),
  };
  const noBlockers = !Object.values(blockers).some(Boolean);
  // log.debug(
  //   "Show Rating Visibility (check)",
  //   "enabled:",
  //   state.chat.enableRating,
  //   "no blockers:",
  //   noBlockers,
  //   "blockers:",
  //   noBlockers ? "-" : blockers
  // );
  return state.chat.enableRating && noBlockers;
};

export default function ConversationRating() {
  const showRating = useTimedSelector(selectShowRating, 1000);

  if (!showRating) {
    return null;
  }
  return <ConversationRatingInner />;
}

const ratingEmojis = ["😠", "🙁", "😐", "😊", "🤩"];
const getRatingLabels = (t: any) => t("rating.labels", { returnObjects: true });

// NOTE(liamvdv): only render the Inner Component if you actually want to show the rating.
// the first render will immediately trigger the API call to offer a rating (i.e. have side effects).
function ConversationRatingInner() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const conversationId = useAppSelector(selectConversationId);
  const conversations = useAppSelector((state) => state.conversations.data);
  const currentConversation = conversations.find(
    (c) => c.conversation_id === conversationId
  );
  const existingScore = currentConversation?.rating_score ?? -1;
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTextarea, setShowTextarea] = useState(false);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedComment, setHasSubmittedComment] = useState(false);
  const [hasOfferedRating, setHasOfferedRating] = useState(false);

  // API Call 1: Offer rating (score=-1 to score=0)
  useEffect(() => {
    if (conversationId && !hasOfferedRating && existingScore === -1) {
      setHasOfferedRating(true);
      dispatch(
        rateConversation({
          conversationId,
          rating: 0, // Offer rating: -1 -> 0
          comment: "",
        })
      );
    }
  }, [conversationId, hasOfferedRating, existingScore, dispatch]);

  // API Call 2: Submit rating when user clicks (score=value)
  const handleRatingClick = async (rating: number) => {
    setSelectedRating(rating);
    setShowTextarea(false);

    // Only dispatch if user hasn't already given rating (score 1-5)
    if (conversationId && existingScore < 1) {
      dispatch(
        rateConversation({
          conversationId,
          rating: rating + 1, // Convert 0-4 index to 1-5 rating
          comment: comment.trim(),
        })
      );
    }
  };

  // API Call 3: Submit rating with comment (score + comment)
  const handleCommentSubmit = async () => {
    if (selectedRating !== null && conversationId) {
      setIsSubmitting(true);
      setError(null);

      try {
        // Only dispatch if user hasn't already given rating with comment
        if (existingScore < 1 || !currentConversation?.rating_comment) {
          await dispatch(
            rateConversation({
              conversationId,
              rating: selectedRating + 1, // Convert 0-4 index to 1-5 rating
              comment: comment.trim(),
            })
          ).unwrap();
        }

        setShowTextarea(false);
        setHasSubmittedComment(true);
      } catch (error: any) {
        setError(error.message || "Failed to submit rating");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleRatingHover = (rating: number | null) => {
    setHoveredRating(rating);
  };

  // If user already gave rating (score 1-5), show success message
  useEffect(() => {
    if (existingScore >= 1 && existingScore <= 5) {
      setSelectedRating(existingScore - 1); // Convert 1-5 to 0-4 index
      if (currentConversation?.rating_comment) {
        setHasSubmittedComment(true);
      }
    }
  }, [existingScore, currentConversation?.rating_comment]);

  return (
    <div className="flex flex-col items-center gap-3 py-4 px-1">
      {selectedRating === null ? (
        <>
          <div className="text-sm text-gray-600 text-center">
            {t("rating.question")}
          </div>
          <div className="flex gap-4">
            {ratingEmojis.map((emoji, index) => (
              <div key={index} className="flex flex-col items-center gap-1">
                <button
                  onClick={() => handleRatingClick(index)}
                  onMouseEnter={() => handleRatingHover(index)}
                  onMouseLeave={() => handleRatingHover(null)}
                  className={`
                    text-2xl p-2 rounded-full transition-all duration-200 
                    ${hoveredRating === index ? "transform scale-125" : "transform scale-100"}
                  `}
                  aria-label={`${t("rating.rateAs")} ${getRatingLabels(t)[index]}`}
                  disabled={isSubmitting}
                >
                  {emoji}
                </button>
                <span
                  className={`text-xs text-gray-500 text-center transition-opacity duration-200 ${
                    hoveredRating === index ? "opacity-100" : "opacity-0"
                  }`}
                >
                  {getRatingLabels(t)[index]}
                </span>
              </div>
            ))}
          </div>
          {error && (
            <div className="text-xs text-red-500 text-center">{error}</div>
          )}
        </>
      ) : showTextarea ? (
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">{ratingEmojis[selectedRating]}</span>
            <span className="text-sm text-gray-600">
              {getRatingLabels(t)[selectedRating]}
            </span>
          </div>
          <div className="space-y-3 ">
            <textarea
              autoFocus
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("rating.commentPlaceholder")}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              rows={3}
              maxLength={1000}
              disabled={isSubmitting}
              name="Rating Comment"
              id="botbrains-rating-comment"
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-400">
                {comment.length}/1000
              </span>
              <button
                onClick={handleCommentSubmit}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-xs bg-brand text-white rounded hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t("rating.continue")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <div className="text-2xl mb-2">
            {selectedRating <= 1 ? "📈" : "🎉"}
          </div>
          <div className="text-lg font-semibold text-brand mb-1">
            {selectedRating <= 1
              ? t("rating.improvementMessage")
              : t("rating.thankYouMessage")}
          </div>
          <div className="text-sm text-gray-500 mb-1">
            {t("rating.ratedAs", {
              rating: getRatingLabels(t)[selectedRating],
            })}
          </div>
          {!hasSubmittedComment && (
            <button
              onClick={() => setShowTextarea(true)}
              className="text-xs text-brand hover:text-brand-hover transition-colors"
            >
              {t("rating.shareDetails")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
